import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fromLonLat, toLonLat } from 'ol/proj.js';
import { useEffect } from 'react';
import { MapPopup, MapPopupRef } from './MapPopup';
import { PopupMapEditor } from './popups/PopupMapEditor';
import {
  useCreateNodeMutation,
  useSplitEdgeMutation,
} from '../services/edgeApi';
import { EditorNode, mapActions } from './slice/mapSlice';
import {
  selectAllEdges,
  selectAllNodes,
  selectEditorEdgeStartNode,
  selectEditorMode,
  selectEditorMoveNode,
  selectEditorNode,
} from './slice/mapSelectors';
import { EdgeCreationOverlay } from './editor/EdgeCreationOverlay';
import { NodePositioningOverlay } from './editor/NodePositioningOverlay';
import { selectNodeSize } from '../plotter/plotterSelectors';
import { Coordinate } from 'ol/coordinate';
import { closestPointOnLine, FeatureLike } from '../utils/geoJsonUtils';
import toast from 'react-hot-toast';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext, MapEventListener } from './context/MapContext';

const getNodeStyle = (size: number) => [
  {
    'circle-radius': size,
    'circle-fill-color': ['get', 'color'],
    'circle-rotate-with-view': false,
    'circle-displacement': [0, 0],
    'circle-opacity': 0.8,
  },
];

export type MapEditorProps = {
  zIndex?: number;
};

export const MapEditor: React.FC<MapEditorProps> = ({ zIndex = 0 }) => {
  const dispatch = useDispatch();
  const [createNodeTrigger, createNodeRequestInfo] = useCreateNodeMutation();
  const [splitEdgeTrigger, splitEdgeRequestInfo] = useSplitEdgeMutation();
  const { setCursorStyle, registerClickListener, unregisterClickListener } =
    useMapContext();

  const contextPopup = useRef<MapPopupRef>(null);

  const nodeSize = useSelector(selectNodeSize);
  const [contextCoords, setContextCoords] = useState<Coordinate | null>(null);

  const edges = useSelector(selectAllEdges);
  const nodes = useSelector(selectAllNodes);

  const mode = useSelector(selectEditorMode);

  const newNode = useSelector(selectEditorNode);
  const edgeStartNode = useSelector(selectEditorEdgeStartNode);
  const editorMoveNode = useSelector(selectEditorMoveNode);

  useEffect(() => {
    const exitEdgeMode = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch(mapActions.setEditorEdgeStartNode(null));
        dispatch(mapActions.setEditorMoveNode(null));
      }
    };
    window.addEventListener('keydown', exitEdgeMode);
    return () => {
      window.removeEventListener('keydown', exitEdgeMode);
    };
  }, [edgeStartNode, dispatch, window]);

  useEffect(() => {
    const enterAddMode = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        dispatch(mapActions.setEditorMode('shift'));
        setCursorStyle('crosshair');
      }
    };
    const exitAddMode = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        dispatch(mapActions.setEditorMode('none'));
        setCursorStyle(null);
      }
    };
    window.addEventListener('keydown', enterAddMode);
    window.addEventListener('keyup', exitAddMode);
    return () => {
      window.removeEventListener('keydown', enterAddMode);
      window.removeEventListener('keyup', exitAddMode);
      setCursorStyle(null);
    };
  }, [setCursorStyle, window, dispatch]);

  const coordinateOnEdge = useCallback(
    (coordinate: Coordinate, edgeId: string) => {
      const edge = edges[edgeId];
      if (!edge) return toLonLat(coordinate);
      const startNode = nodes[edge.start_node];
      const endNode = nodes[edge.end_node];
      if (!startNode || !endNode) return toLonLat(coordinate);
      return closestPointOnLine(coordinate, [
        fromLonLat([Number(startNode.lng), Number(startNode.lat)]),
        fromLonLat([Number(endNode.lng), Number(endNode.lat)]),
      ]);
    },
    [edges, nodes],
  );

  useEffect(() => {
    const listener: MapEventListener = (feature, coordinate) => {
      const isEdge =
        feature?.get('key')?.startsWith('edge-') && mode === 'shift';
      const edgeId = feature?.get('id');
      const isMovingNode = editorMoveNode !== null;
      if ((feature && !isEdge) || isMovingNode) {
        contextPopup.current?.hide();
        dispatch(mapActions.setEditorNode(null));
        return;
      }

      if (isEdge) {
        const coords = toLonLat(coordinateOnEdge(coordinate, edgeId));
        dispatch(
          mapActions.setEditorNode({
            lng: coords[0].toString(),
            lat: coords[1].toString(),
            splitEdgeId: edgeId,
            tspId: edges[edgeId]?.tsp_id,
          }),
        );
      } else {
        const coords = toLonLat(coordinate);
        setContextCoords(coords);
        contextPopup.current?.show(coordinate);
      }
    };
    registerClickListener(listener);
    return () => {
      unregisterClickListener(listener);
    };
  }, [
    registerClickListener,
    unregisterClickListener,
    editorMoveNode,
    mode,
    coordinateOnEdge,
  ]);

  useEffect(() => {
    if (newNode) {
      const coords = fromLonLat([
        Number(newNode.lng) ?? 0,
        Number(newNode.lat) ?? 0,
      ]);
      contextPopup.current?.show(coords);
    } else {
      contextPopup.current?.hide();
    }
  }, [newNode]);

  const nodeFeatures = useMemo(() => {
    const features: FeatureLike[] = [];
    if (newNode) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: fromLonLat([
            Number(newNode.lng) ?? 0,
            Number(newNode.lat) ?? 0,
          ]),
        },
        properties: {
          key: `new-node`,
          type: 'node',
          color: '#2d8d00',
        },
      });
    }
    return features;
  }, [newNode]);

  const style = useMemo(() => getNodeStyle(Number(nodeSize) ?? 0), [nodeSize]);

  useMapLayer(nodeFeatures, { zIndex, style });

  const createNode = async (newNode: EditorNode) => {
    if (newNode.splitEdgeId) {
      const response = await splitEdgeTrigger({
        edgeId: Number(newNode.splitEdgeId),
        node: {
          lat: newNode.lat?.toString(),
          lng: newNode.lng?.toString(),
          points: newNode.points ?? [],
        },
      });
      if (!response) {
        console.error('Edge splitting failed');
        return;
      }
    } else {
      const node = await createNodeTrigger({
        lat: newNode.lat?.toString(),
        lng: newNode.lng?.toString(),
        points: newNode.points ?? [],
      }).unwrap();

      if (!node) {
        console.error('Node creation failed');
        return;
      }
    }
    dispatch(mapActions.setEditorNode(null));
    contextPopup.current?.hide();
    setContextCoords(null);
  };

  const startNodeCreation = () => {
    dispatch(
      mapActions.setEditorNode({
        lng: contextCoords?.[0].toString(),
        lat: contextCoords?.[1].toString(),
      }),
    );
  };

  const cancelNodeCreation = () => {
    dispatch(mapActions.setEditorNode(null));
  };

  const addNodePoint = (pointId: number) => {
    if (!newNode) return;
    if (newNode.points?.includes(pointId)) {
      toast.error('Point already added to the node');
      return;
    } else {
      dispatch(
        mapActions.setEditorNode({
          ...newNode,
          points: [...(newNode?.points ?? []), pointId],
        }),
      );
    }
  };

  const removeNodePoint = (pointId: number) => {
    if (!newNode) return;
    dispatch(
      mapActions.setEditorNode({
        ...newNode,
        points: newNode.points?.filter((id) => pointId !== id),
      }),
    );
  };

  return (
    <>
      <EdgeCreationOverlay />
      <NodePositioningOverlay />
      <MapPopup
        ref={contextPopup}
        closeButton={false}
        onClose={() => contextPopup.current?.hide()}
      >
        <PopupMapEditor
          mode={mode}
          coords={contextCoords ?? [0, 0]}
          isLoading={
            createNodeRequestInfo.isLoading || splitEdgeRequestInfo.isLoading
          }
          onCreate={createNode}
          onCreateStart={startNodeCreation}
          onCreateCancel={cancelNodeCreation}
          onAddPoint={addNodePoint}
          onRemovePoint={removeNodePoint}
        />
      </MapPopup>
    </>
  );
};
